import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Minus, Coffee } from 'lucide-react';
import { DropdownItemOption } from './ItemDropdown';
import { ItemDropdown } from './ItemDropdown';
import { tg } from '../../telegram';

interface AddShiftSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: DropdownItemOption[];
  onAdd: (item: { product_name: string; quantity: number }) => void;
}

export const AddShiftSaleModal: React.FC<AddShiftSaleModalProps> = ({
  isOpen,
  onClose,
  products,
  onAdd,
}) => {
  const [selectedName, setSelectedName] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);

  useEffect(() => {
    if (isOpen) {
      setSelectedName('');
      setQuantity(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleSelectProduct = (name: string) => {
    setSelectedName(name);
  };

  const currentQty = Math.max(1, quantity);
  const isValid = Boolean(selectedName.trim() && currentQty >= 1);

  const handleConfirmAdd = () => {
    if (!isValid) return;
    tg.haptic.success();
    onAdd({
      product_name: selectedName.trim(),
      quantity: currentQty,
    });
    onClose();
  };

  const handleQuickAddQty = (delta: number) => {
    tg.haptic.impact('light');
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  const matchedProduct = products.find(
    (p) => p.name.toLowerCase() === selectedName.trim().toLowerCase()
  );

  return createPortal(
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9990,
        backgroundColor: 'rgba(21, 17, 14, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        className="modal-sheet"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          backgroundColor: 'var(--bg-surface)',
          borderRadius: '20px 20px 0 0',
          padding: '16px 20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: 'var(--shadow-lg)',
          overflowY: 'auto',
          animation: 'slideUp 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Handle */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'var(--border-color)',
            }}
          />
        </div>

        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--primary-accent-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--primary-accent)',
              }}
            >
              <Coffee size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>
                Thêm Món Bán Trong Ca
              </h3>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Ghi nhận số lượng đồ uống đã bán qua máy POS
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="stepper-btn"
            style={{ width: 32, height: 32 }}
            title="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* 1. Chọn món đồ uống */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                marginBottom: 6,
              }}
            >
              Món Đồ Uống: <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <ItemDropdown
              value={selectedName}
              items={products}
              placeholder="Gõ để tìm kiếm món (Cà phê, Trà sữa, Sinh tố...)..."
              onChange={handleSelectProduct}
            />
          </div>

          {/* 2. Số lượng bán */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                marginBottom: 6,
              }}
            >
              Số Lượng Bán (Ly): <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setQuantity((q) => Math.max(1, q - 1));
                }}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 18 }}
              >
                <Minus size={18} />
              </button>

              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="number"
                  min="1"
                  className="input num"
                  value={quantity === 0 ? '' : quantity}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value);
                    setQuantity(isNaN(val) ? 1 : Math.max(0, val));
                  }}
                  onBlur={() => {
                    if (quantity < 1) setQuantity(1);
                  }}
                  style={{
                    textAlign: 'center',
                    fontWeight: 800,
                    fontSize: 20,
                    minHeight: 44,
                    height: 44,
                    color: 'var(--text-primary)',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--text-tertiary)',
                    pointerEvents: 'none',
                  }}
                >
                  ly
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setQuantity((q) => q + 1);
                }}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 18 }}
              >
                <Plus size={18} />
              </button>
            </div>

            {/* Quick Increment Chips */}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {[1, 2, 5, 10].map((inc) => (
                <button
                  key={inc}
                  type="button"
                  onClick={() => handleQuickAddQty(inc)}
                  className="badge badge-neutral"
                  style={{
                    flex: 1,
                    padding: '6px 0',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    color: 'var(--text-primary)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  +{inc} ly
                </button>
              ))}
            </div>
          </div>

          {/* Item Preview Card */}
          {selectedName.trim() && (
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-surface-subtle)',
                border: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {selectedName}
                </div>
                {matchedProduct?.category && (
                  <span
                    className="badge badge-neutral"
                    style={{ fontSize: 10, marginTop: 4, display: 'inline-block' }}
                  >
                    {matchedProduct.category}
                  </span>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--primary-accent)' }}>
                  {currentQty}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 4 }}>
                  ly
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            style={{ minHeight: 46 }}
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleConfirmAdd}
            disabled={!isValid}
            className="btn btn-primary"
            style={{
              minHeight: 46,
              opacity: isValid ? 1 : 0.5,
              cursor: isValid ? 'pointer' : 'not-allowed',
            }}
          >
            <Plus size={18} /> Thêm Vào Danh Sách Bán
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
